import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Divider,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import 'flag-icons/css/flag-icons.min.css';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import httpClient from '../../utils/httpClient';
import { WgApplicationsEnum } from '../../utils/WgApplicationsEnum';
import { completeRegistrationFromPreRegistration } from './complete-pre-register.api';

const CompletePreRegister: React.FC = () => {
  const { preRegistrationGuid } = useParams<{ preRegistrationGuid: string }>();
  const navigate = useNavigate();

  // Area 1 - Company/Customer
  const [fantasyName, setFantasyName] = useState('');
  const [registrationName, setRegistrationName] = useState('');
  const [registrationId, setRegistrationId] = useState('');
  const [responsableName, setResponsableName] = useState('');
  const [responsableEmail, setResponsableEmail] = useState('');

  // Area 2 - User access
  const [sameAsResponsible, setSameAsResponsible] = useState<'yes' | 'no'>('yes');
  const [userName, setUserName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [passwordRaw, setPasswordRaw] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('');

  type Country = { name: string; code: string; className: string };
  const countryList: Array<Country> = [
    { name: 'Argentina', code: 'AR', className: 'fi fi-ar' },
    { name: 'Austrália', code: 'AU', className: 'fi fi-au' },
    { name: 'Brasil', code: 'BR', className: 'fi fi-br' },
    { name: 'Canadá', code: 'CA', className: 'fi fi-ca' },
    { name: 'Colômbia', code: 'CO', className: 'fi fi-co' },
    { name: 'Chile', code: 'CL', className: 'fi fi-cl' },
    { name: 'China', code: 'CN', className: 'fi fi-cn' },
    { name: 'França', code: 'FR', className: 'fi fi-fr' },
    { name: 'Índia', code: 'IN', className: 'fi fi-in' },
    { name: 'Japão', code: 'JP', className: 'fi fi-jp' },
    { name: 'México', code: 'MX', className: 'fi fi-mx' },
    { name: 'Portugal', code: 'PT', className: 'fi fi-pt' },
    { name: 'Espanha', code: 'ES', className: 'fi fi-es' },
    { name: 'Estados Unidos', code: 'US', className: 'fi fi-us' }
  ];

  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (sameAsResponsible === 'yes') {
      setUserName(responsableName);
      setEmailAddress(responsableEmail);
    }
  }, [sameAsResponsible, responsableName, responsableEmail]);

  const formatCNPJ = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    const p0 = digits.slice(0, 2);
    const p1 = digits.slice(2, 5);
    const p2 = digits.slice(5, 8);
    const p3 = digits.slice(8, 12);
    const p4 = digits.slice(12, 14);
    let out = p0;
    if (digits.length > 2) out += '.' + p1;
    if (digits.length > 5) out += '.' + p2;
    if (digits.length > 8) out += '/' + p3;
    if (digits.length > 12) out += '-' + p4;
    return out;
  };

  // Load existing pre-register info from API using preRegistrationGuid
  useEffect(() => {
    const gid = (preRegistrationGuid || '').trim();
    if (!gid) return;

    const fetchCustomer = async () => {
      setIsLoading(true);
      setApiErrors([]);
      try {
        const res = await httpClient.get(`/api/v1/portal/enrollment/pre-register/${encodeURIComponent(gid)}`);
        const payload = res?.data;
        const dataObj = (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object')
          ? payload.data
          : null;

        if (!dataObj) {
          setApiErrors(['Dados do cliente não encontrados para o identificador informado.']);
          return;
        }

        const fantasy = (dataObj.fantasyName || '') as string;
        const regName = (dataObj.registrationName || '') as string;
        const regId = (dataObj.registrationId || '') as string;
        const respName = (dataObj.responsableName || '') as string;
        const respEmail = (dataObj.responsableEmail || '') as string;

        setFantasyName(fantasy);
        setRegistrationName(regName);
        setRegistrationId(formatCNPJ(String(regId)));
        setResponsableName(respName);
        setResponsableEmail(respEmail);
      } catch (err: any) {
        const respData = err?.response?.data;
        const messages: string[] = [];
        if (respData) {
          if (Array.isArray(respData.errors)) {
            for (const item of respData.errors) {
              const msg = (item && (item.errorMessage || item.message)) as string | undefined;
              if (msg) messages.push(msg);
            }
          }
        }
        setApiErrors(messages.length ? messages : ['Erro ao carregar dados do cliente.']);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preRegistrationGuid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiErrors([]);
    setSuccessMessage('');

    if (!responsableName || !responsableEmail || !userName || !emailAddress || !passwordRaw || !fantasyName || !countryCode) {
      setApiErrors(['Por favor, preencha os campos obrigatórios.']);
      return;
    }

    const payload = {
      userName: userName.trim(),
      emailAddress: emailAddress.trim(),
      password: passwordRaw.trim(),
      responsableName: responsableName.trim(),
      responsableEmail: responsableEmail.trim(),
      applications: [WgApplicationsEnum.IMMIGRATION_CANADA],
      fantasyName: fantasyName.trim() || undefined,
      registrationName: registrationName.trim() || undefined,
      registrationId: registrationId.replace(/\D/g, '') || undefined,
      customerCountry: countryCode || undefined,
    };

    try {
      const gid = (preRegistrationGuid || '').trim();
      const res = await completeRegistrationFromPreRegistration(gid, payload);

      // Success shape: { success: true, status: 100, data: '<guid>' }
      // Error shape: may include errors array; handle both cases robustly
      const errors = (res as any)?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        const msgs = errors.map((e: any) => e?.errorMessage).filter(Boolean);
        setApiErrors(msgs.length ? msgs : ['Falha ao completar cadastro.']);
        return;
      }

      if (res?.success && typeof res?.data === 'string' && res.data) {
        setSuccessMessage('Perfil ativado com sucesso! Você será redirecionado para o Login.');
        // Redirect after short delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const message = res?.message || 'Falha ao completar cadastro.';
        setApiErrors([message]);
      }
    } catch (err: any) {
      const respData = err?.response?.data;
      const collected: string[] = [];
      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) collected.push(msg);
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          collected.push(respData.message);
        }
      }
      setApiErrors(collected.length ? collected : ['Ocorreu um erro ao completar seu cadastro.']);
      console.error('Error completing pre-register:', err);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 6 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon fontSize="large" sx={{ mr: 1 }} />
            Completar Pré-Cadastro
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Preencha os dados abaixo para completar seu cadastro na plataforma. Utilize o link recebido com seu identificador único.
          </Typography>
          {/* Removed customer GUID info alert as requested */}

          {apiErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {apiErrors.map((msg, idx) => (
                <Alert key={idx} severity="error" sx={{ mb: 1 }}>{msg}</Alert>
              ))}
            </Box>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Area 1: Company/Customer */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Dados da Empresa/Cliente
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Nome da sua empresa"
                  value={fantasyName}
                  onChange={(e) => setFantasyName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText="Máximo de 80 caracteres"
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="country-select-label" shrink required>País</InputLabel>
                  <Select
                    labelId="country-select-label"
                    value={countryCode}
                    label="País"
                    onChange={(e) => setCountryCode(e.target.value)}
                    displayEmpty
                    notched
                    required
                    renderValue={(val) => {
                      if (!val) {
                        return (
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Selecione o País
                          </Typography>
                        );
                      }
                      const c = countryList.find(x => x.code === val);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span className={c?.className} />
                          <Typography variant="body1">{c?.name}</Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Selecione o País
                      </Typography>
                    </MenuItem>
                    {countryList.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span className={c.className} />
                          <Typography variant="body1">{c.name}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Razão Social"
                  value={registrationName}
                  onChange={(e) => setRegistrationName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Registro (CNPJ/ID)"
                  value={registrationId}
                  onChange={(e) => setRegistrationId(formatCNPJ(e.target.value))}
                  placeholder="00.000.000/0000-00"
                  helperText="Formato: 00.000.000/0000-00"
                  inputProps={{ maxLength: 80, inputMode: 'numeric' }}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Nome do Responsável"
                  value={responsableName}
                  onChange={(e) => setResponsableName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText="Máximo de 80 caracteres"
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label="E-mail do Responsável"
                  value={responsableEmail}
                  onChange={(e) => setResponsableEmail(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText="Máximo de 80 caracteres"
                />
              </Grid>
            </Grid>

            {/* Area 2: User Access */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 4, mb: 1 }}>
              Acesso do Usuário
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <RadioGroup
              row
              value={sameAsResponsible}
              onChange={(e) => setSameAsResponsible(e.target.value as 'yes' | 'no')}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Usar nome e e-mail do responsável" />
              <FormControlLabel value="no" control={<Radio />} label="Definir nome e e-mail do usuário diferentes" />
            </RadioGroup>

            <Grid container spacing={2}>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Nome do Usuário"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText="Máximo de 80 caracteres"
                  disabled={sameAsResponsible === 'yes'}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label="Email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  helperText="Máximo de 80 caracteres"
                  disabled={sameAsResponsible === 'yes'}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Senha"
                  value={passwordRaw}
                  onChange={(e) => setPasswordRaw(e.target.value)}
                  inputProps={{ maxLength: 15 }}
                  helperText="Máximo de 15 caracteres"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/login')}>Voltar</Button>
              <Button type="submit" variant="contained" startIcon={<CheckCircleIcon />}>Completar cadastro</Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CompletePreRegister;